import { useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { pick } from 'lodash'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import AddressAutocomplete from 'components/AddressAutocomplete'
import { HomeIcon } from 'components/Icons'
import MultiFileUpload from 'components/MultiFileUpload'
import { useSession } from 'components/Providers'
import { create, update } from 'store/academic/parent/address-change'
import formatAddress from 'utils/formatAddress'
import { uploadFile } from 'utils/file'
import { FILE_DELETED, MIN_PROOF_FILES, proofList } from 'utils/proofOfAddress'
import * as snackbar from 'utils/snackbar'

const PROOF_STEP = 2

const PROOF_INSTRUCTIONS = `3 proofs of current address:
1. Current lease or deed
2. Driver's License
3. Utility bill or Bank statement`

// const ADDRESS_TYPES = ['home', 'work']

const EMPTY_ADDRESS = {
  type: 'home',
  street: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
}

const REQUIRED_ADDRESS_FIELDS = ['type', 'street', 'city', 'state', 'zip', 'country']

const names = people => people.map(person => `${person.firstName} ${person.lastName}`).join(', ')

const initials = person => `${person.firstName?.[0] ?? ''}${person.lastName?.[0] ?? ''}`.toUpperCase()

const placeToAddress = place => {
  const get = type => place.address_components?.find(item => item.types.includes(type))?.short_name ?? ''
  return {
    street: [get('street_number'), get('route')].filter(Boolean).join(' '),
    city: get('locality') || get('sublocality'),
    state: get('administrative_area_level_1'),
    zip: get('postal_code'),
    country: get('country'),
  }
}

const SectionTitle = ({ title, caption }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="subtitle2">{title}</Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    )}
  </Box>
)

const SummaryRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
)

const sameId = (a, b) => String(a) === String(b)

export default function AddressChangeRequestForm({ memberId, options, request, contacts, onClose, onSaved, onConflict }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { person } = useSession()
  const isEdit = Boolean(request)

  const [activeStep, setActiveStep] = useState(isEdit ? PROOF_STEP : 0)
  const [secondaryIds, setSecondaryIds] = useState(() => (request?.secondaryParentIds ?? []).map(String))
  const [address, setAddress] = useState(() =>
    request?.address
      ? { ...EMPTY_ADDRESS, ...pick(request.address, Object.keys(EMPTY_ADDRESS)), line2: request.address.line2 ?? '' }
      : EMPTY_ADDRESS,
  )
  const [keptFiles, setKeptFiles] = useState(() => proofList(request?.proofOfAddress))
  const [newFiles, setNewFiles] = useState([])
  const [saving, setSaving] = useState(false)
  const [incomplete, setIncomplete] = useState(false)

  const steps = [t('Students'), t('New Address'), t('Proof of Address')]

  const ownStudents = options?.students ?? []
  const secondaryParents = options?.secondaryParents ?? []
  const selectedParents = secondaryParents.filter(parent => secondaryIds.includes(String(parent.id)))

  const students = [...ownStudents]
  selectedParents.forEach(parent =>
    parent.students.forEach(student => {
      if (!students.some(item => sameId(item.id, student.id))) students.push(student)
    }),
  )
  const isOwnStudent = student => ownStudents.some(item => sameId(item.id, student.id))

  const currentAddress = formatAddress(
    (contacts ?? []).find(item => sameId(item?.contact?.id, person?.id))?.contact?.addresses?.[0],
  )

  const toggleParent = id =>
    setSecondaryIds(prev => (prev.includes(id) ? prev.filter(value => value !== id) : [...prev, id]))

  const setField = (key, value) => setAddress(prev => ({ ...prev, [key]: value }))

  const onPlaceSelected = place => {
    const found = placeToAddress(place)
    setAddress(prev => ({ ...prev, ...found }))
    setIncomplete(Object.values(found).some(value => !value))
  }

  const missingAddressFields = REQUIRED_ADDRESS_FIELDS.filter(key => !String(address[key] ?? '').trim())
  const hasAddress = missingAddressFields.length === 0
  // Deleted files (removed by the parent or rejected by the school) stay in the payload but are hidden
  const shownFiles = keptFiles.filter(item => item.fileStatus !== FILE_DELETED)
  const validProofCount = shownFiles.length + newFiles.length
  const hasProof = validProofCount >= MIN_PROOF_FILES
  const canContinue = activeStep === 0 ? students.length > 0 : missingAddressFields.length === 0

  const proofItems = [
    ...shownFiles.map(item => ({ key: `kept-${item.fileId}`, name: item.name, uuid: item.fileUuid, kept: item })),
    ...newFiles.map((file, index) => ({ key: `new-${index}-${file.name}`, name: file.name, file })),
  ]

  const removeProof = item =>
    item.file
      ? setNewFiles(prev => prev.filter(file => file !== item.file))
      : setKeptFiles(prev => prev.map(kept => (kept === item.kept ? { ...kept, fileStatus: FILE_DELETED } : kept)))

  const uploadProof = async () => {
    const uploaded = []
    for (const file of newFiles) {
      const saved = await uploadFile(file)
      if (!saved) return null
      uploaded.push({ fileId: saved.id, fileUuid: saved.uuid, name: saved.name ?? file.name, fileStatus: 'uploaded' })
    }
    return [...keptFiles, ...uploaded]
  }

  const onSubmit = async () => {
    setSaving(true)
    try {
      const proofOfAddress = await uploadProof()
      if (!proofOfAddress) return

      const res = await dispatch(
        isEdit
          ? update({ memberId, id: request.id, proofOfAddress })
          : create({
              memberId,
              studentIds: students.map(student => Number(student.id)),
              secondaryParentIds: selectedParents.length ? selectedParents.map(parent => Number(parent.id)) : null,
              address: { ...address, line2: address.line2 || null, type: 'home' },
              proofOfAddress,
            }),
      )
      if (res.payload?.id) {
        snackbar.success(t(isEdit ? 'Address change request updated' : 'Address change request submitted'), {
          variant: 'success',
        })
        onSaved?.()
      } else if (res.error?.status === 409) {
        onConflict?.()
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box data-component="AddressChangeRequestForm" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title={isEdit ? t('Edit Address Change Request') : t('Request Address Change')}
        small
        close={onClose}
        borderBottom
      />

      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2 }}>
        {activeStep === 0 && (
          <Stack spacing={3}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}
            >
              <HomeIcon color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('Current address')}
                </Typography>
                <Typography variant="body2">{currentAddress || t('No address on file')}</Typography>
              </Box>
            </Stack>

            <Box>
              <SectionTitle
                title={t('Students')}
                caption={t('All the students you are a guardian of are included in the request')}
              />
              <Paper variant="outlined">
                {students.length ? (
                  students.map((student, index) => (
                    <Stack
                      key={student.id}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ px: 1.5, py: 1, borderTop: index ? 1 : 0, borderColor: 'divider' }}
                    >
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{initials(student)}</Avatar>
                      <Box>
                        <Typography variant="body2">
                          {student.firstName} {student.lastName}
                        </Typography>
                        {!isOwnStudent(student) && (
                          <Typography variant="caption" color="text.secondary">
                            {t('Added with the other parent')}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ))
                ) : (
                  <Typography variant="body2" color="text.disabled" sx={{ p: 1.5 }}>
                    {t('No students found')}
                  </Typography>
                )}
              </Paper>
            </Box>

            <Box>
              <SectionTitle title={t('Other Parents')} />
              {secondaryParents.length ? (
                <Stack spacing={1}>
                  {secondaryParents.map(parent => {
                    const id = String(parent.id)
                    const selected = secondaryIds.includes(id)
                    const name = `${parent.firstName} ${parent.lastName}`
                    return (
                      <Paper
                        key={id}
                        variant="outlined"
                        sx={{
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? theme => alpha(theme.palette.primary.main, 0.04) : 'transparent',
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0, width: '100%', alignItems: 'flex-start', px: 1, py: 0.5 }}
                          control={<Checkbox checked={selected} onChange={() => toggleParent(id)} />}
                          label={
                            <Box sx={{ pt: 1 }}>
                              <Typography variant="body2">
                                {parent.students.length
                                  ? t('Apply this change to {{name}} and the following children they are the primary parent of', { name })
                                  : t('Apply this change to {{name}}', { name })}
                              </Typography>
                              {parent.students.length > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {names(parent.students)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </Paper>
                    )
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  {t('No other parents on file')}
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {activeStep === 1 && (
          <Grid container spacing={2}>
            {/* <Grid item xs={12}>
              <SectionTitle title={t('Type')} />
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                color="primary"
                value={address.type}
                onChange={(event, value) => value && setField('type', value)}
              >
                {ADDRESS_TYPES.map(type => (
                  <ToggleButton key={type} value={type}>
                    {t(type === 'home' ? 'Home' : 'Work')}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid> */}
            <Grid item xs={12}>
              <AddressAutocomplete label={t('New Address')} onPlaceSelected={onPlaceSelected} />
              {incomplete && (
                <FormHelperText error>
                  {t('The selected address is incomplete, search for a more specific one')}
                </FormHelperText>
              )}
            </Grid>

            {hasAddress && (
              <>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <LocationOnIcon color="action" />
                      <Typography variant="body2">
                        {[formatAddress({ ...address, line2: null }), address.country].filter(Boolean).join(', ')}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('Apartment, suite, etc.')}
                    value={address.line2}
                    onChange={event => setField('line2', event.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}

        {activeStep === PROOF_STEP && (
          <Stack spacing={3}>
            <Box>
              <SectionTitle
                title={t('Address Change Request')}
                caption={
                  isEdit
                    ? t('Only the proof of address can be changed. To change anything else, cancel this request and submit a new one.')
                    : null
                }
              />
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={1.5}>
                  <SummaryRow label={t('New Address')} value={formatAddress(isEdit ? request.address : address)} />
                  <SummaryRow label={t('Students')} value={names(isEdit ? request.students ?? [] : students)} />
                  {(isEdit ? request.secondaryParents ?? [] : selectedParents).length > 0 && (
                    <SummaryRow
                      label={t('Other Parents')}
                      value={names(isEdit ? request.secondaryParents : selectedParents)}
                    />
                  )}
                </Stack>
              </Paper>
            </Box>

            <Box>
              <SectionTitle
                title={t('Proof of Address')}
                caption={t('Attach at least {{count}} documents showing the new address.', { count: MIN_PROOF_FILES })}
              />
              <MultiFileUpload
                items={proofItems}
                onAdd={files => setNewFiles(prev => [...prev, ...files])}
                onRemove={removeProof}
                min={MIN_PROOF_FILES}
                validCount={validProofCount}
                instructions={t(PROOF_INSTRUCTIONS)}
                disabled={saving}
              />
            </Box>

            <Alert severity="info">
              {t('Your request will be reviewed by the school before the address is updated.')}
            </Alert>
          </Stack>
        )}
      </Box>

      <Divider />
      <Stack direction="row" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
        <Button
          onClick={activeStep === 0 || isEdit ? onClose : () => setActiveStep(step => step - 1)}
          disabled={saving}
        >
          {activeStep === 0 || isEdit ? t('Cancel') : t('Back')}
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" disabled={!canContinue} onClick={() => setActiveStep(step => step + 1)}>
            {t('Next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={saving || !hasProof}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isEdit ? t('Save Changes') : t('Submit Request')}
          </Button>
        )}
      </Stack>
    </Box>
  )
}
